import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../Keyvault/SearchContext';
import { getTenantList, decommissionTenant, suspensionTenant, reactivationTenant } from '../../apis/tenantDetailsApi/tenantDetailsApi';
import { checkRolePermissions } from '../../utils/HasPermission';

interface TenantItem {
  Id: string;
  Tenant_Nm: string;
  Tenant_Desc: string;
  KeyVault_Id: string | null;
  Tenant_Status: string
}

const TenantList: React.FC = () => {
  const isReadEnabled = checkRolePermissions("Tenant", false, "read")
  const isUpdateEnabled = checkRolePermissions("Tenant", false, "update")
  const isDeleteEnabled = checkRolePermissions("Tenant", false, "delete")

  const navigate = useNavigate();
  const { searchText } = useSearch();
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTenant, setMenuTenant] = useState<TenantItem | null>(null);
  const [isDecommissionOpen, setIsDecommissionOpen] = useState(false);
  const [decommissionLoading, setDecommissionLoading] = useState(false);
  const [isSuspensionOpen, setIsSuspensionOpen] = useState(false);
  const [suspensionLoading, setSuspensionLoading] = useState(false);
  const [isReactivationOpen, setIsReactivationOpen] = useState(false);
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const [decomissionError, setDecomissionError] : any = useState({});
  const [isDecomissionError, setDError] : any = useState(false);

  const [selectedTennat, setSelectedTennat] = useState({})

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTenantList();
      const tenantArray = Array.isArray(response.data) ? response.data : [];
      setTenants(
        tenantArray.map((item: any) => ({
          Id: item.Id,
          Tenant_Nm: item.Tenant_Nm,
          Tenant_Desc: item.Tenant_Desc,
          KeyVault_Id: item.TenantKeyVault_Id ?? null,
          Tenant_Status: item?.Tenant_Status || "-"
        }))
      );
    } catch (err: any) {
      console.error('Error fetching tenants:', err);
      setError('Failed to load tenant list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    if (!isReadEnabled) {
      setError("You dont have permission to view Enviornment List")
    }
  }, []);

  const filteredTenants = useMemo(() => {
    if (!searchText) return tenants;
    return tenants.filter(tenant =>
      Object.values(tenant).some(
        value =>
          value &&
          value.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [tenants, searchText]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>, tenant: TenantItem) => {
    setAnchorEl(event.currentTarget);
    setMenuTenant(tenant);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (!menuTenant) return;
    handleClose();
    navigate(`/TenantDetails/${menuTenant.Tenant_Nm}`, {
      state: { envDetails: menuTenant },
    });
  };

  const handlePostProvision = () => {
    if (!menuTenant) return;
    handleClose();
    navigate(`/tenants/configure/${menuTenant.Id}?name=${menuTenant.Tenant_Nm}`);
  };


  const handleDecommission = () => {
    menuTenant && setSelectedTennat(menuTenant)
    setAnchorEl(null); // Close the menu
    setIsDecommissionOpen(true);
  };

  const handleSuspension = () => {
    setAnchorEl(null);
    setIsSuspensionOpen(true);
  };

  const handleReactivation = () => {
    setAnchorEl(null);
    setIsReactivationOpen(true);
  };

  const handleCloseDecommissionModal = () => {
    setIsDecommissionOpen(false);
  };

  const confirmDecommissionAction = async () => {
    if (!menuTenant) return;

    setDecommissionLoading(true);
    try {
      const response = await decommissionTenant(Number(menuTenant.Id));
      console.log("Tenant decommissioned successfully:", menuTenant.Tenant_Nm);
      handleCloseDecommissionModal();
      await loadData();
    } catch (obj: any) {
      const apiResponse = obj.response.data
      const result = {
        Message: apiResponse?.message ?? null,
        TenantpipelineStatus:
          apiResponse?.pipelineResults
            ?.filter((x:any) => x?.sourceType === "TENANT")
            ?.map((x:any) => x?.status ?? null) ?? [],
        InstancepipelineStatus:
          apiResponse?.pipelineResults
            ?.filter((x:any) => x?.sourceType === "INSTANCE")
            ?.map((x:any) => x?.status ?? null) ?? [],
        TenantdecommissionStatus:
          apiResponse?.decommissionStatus?.tenant?.statusMessage ?? null,
        InstancedecommissionStatus:
          apiResponse?.decommissionStatus?.instances
            ?.map((x:any) => x?.statusMessage ?? null) ?? []
      };
      setDecomissionError(result)
      setDError(true)
      handleCloseDecommissionModal();
      // alert(`Failed to decommission tenant: ${err.message || 'Unknown error'}`);
    } finally {
      setDecommissionLoading(false);
    }
  };

  const confirmSuspensionAction = async () => {
    if (!menuTenant) return;

    setSuspensionLoading(true);
    try {
      await suspensionTenant(Number(menuTenant.Id));

      console.log("Tenant suspended successfully:", menuTenant.Tenant_Nm);
      setIsSuspensionOpen(false);
      await loadData();
    } catch (err: any) {
      console.error("Tenant suspension failed:", err);
      alert(`Failed to suspend tenant: ${err.message || 'Unknown error'}`);
    } finally {
      setSuspensionLoading(false);
    }
  };

  const confirmReactivationAction = async () => {
    if (!menuTenant) return;

    setReactivationLoading(true);
    try {
      await reactivationTenant(Number(menuTenant.Id));

      console.log("Tenant reactivated successfully:", menuTenant.Tenant_Nm);
      setIsReactivationOpen(false);
      await loadData();
    } catch (err: any) {
      console.error("Tenant reactivating failed:", err);
      alert(`Failed to reactivate tenant: ${err.message || 'Unknown error'}`);
    } finally {
      setReactivationLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 6 }}>
      {isDecomissionError && <Snackbar
        open={true}
        autoHideDuration={3000}
        onClose={()=>setDError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={()=>{}}
            severity="success"
            sx={{ position : "absolute", top : "-650px", right : "-650px"}}>
              <div style={{fontWeight : 700, font : "18px"}}>
                {decomissionError.Message} 
              </div>
              {/* <div style={{fontWeight : 500, font : "14px"}}>
                Tenant Decommission Status - {decomissionError.TenantdecommissionStatus} - {decomissionError.TenantpipelineStatus}
              </div>
               <div style={{fontWeight : 500, font : "14px"}}>
                Instance Decommission Status - {decomissionError.Message} - {decomissionError.InstancepipelineStatus}
              </div> */}
          </Alert>
      </Snackbar>}
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ color: '#333' }}>
          Tenant List
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          sx={{
            backgroundColor: '#734bd1',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#5e3fb0' },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress sx={{ color: '#734bd1' }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography color="error">{error}</Typography>
            </Box>
          ) : filteredTenants.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography>No tenants found</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} 
              sx={{
                maxHeight: 380, 
              }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120, backgroundColor: '#e0d3ff' }}>ID</TableCell>
                    <TableCell sx={{ width: 180, backgroundColor: '#e0d3ff' }}>Tenant Name</TableCell>
                    <TableCell sx={{ width: 300, backgroundColor: '#e0d3ff' }}>Description</TableCell>
                    <TableCell sx={{ width: 220, backgroundColor: '#e0d3ff' }}>Keyvault ID</TableCell>
                    <TableCell sx={{ width: 220, backgroundColor: '#e0d3ff' }}>Tenant Status</TableCell>
                    <TableCell sx={{ width: 140, backgroundColor: '#e0d3ff' }} align="center">
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTenants.map((tenant, index) => (
                    
                    <TableRow key={index} hover>
                      <TableCell>{tenant.Id}</TableCell>
                      <TableCell>{tenant.Tenant_Nm}</TableCell>
                      <TableCell>{tenant.Tenant_Desc}</TableCell>
                      <TableCell>
                        {tenant.KeyVault_Id ?? <em style={{ color: '#999' }}>null</em>}
                      </TableCell>
                      <TableCell>{tenant.Tenant_Status}</TableCell>
                      <TableCell align="center">
                        <IconButton onClick={(e) => handleOpen(e, tenant)}>
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && menuTenant === tenant}
                          onClose={handleClose}
                        >
                          <MenuItem onClick={handleView}>View</MenuItem>
                          {tenant?.Tenant_Status !== "Decommissioned" && tenant?.Tenant_Status !== "Provisioning Failed" && <MenuItem onClick={isUpdateEnabled ? handlePostProvision : () => { }} style={{ color: !isUpdateEnabled ? "#0000001f" : "" }}>Post Provision</MenuItem>}
                          {tenant?.Tenant_Status !== "Decommissioned" && tenant?.Tenant_Status !== "Provisioning Failed" && tenant?.Tenant_Status !== "Provisioning" && <MenuItem onClick={isDeleteEnabled ? handleDecommission : () => { }} sx={{ color: 'red' }} style={{ color: !isDeleteEnabled ? "#0000001f" : "" }}>Decommission</MenuItem>}
                          {tenant?.Tenant_Status !== "Decommissioned" && tenant?.Tenant_Status !== "Provisioning Failed" && tenant?.Tenant_Status !== "Suspended" && tenant?.Tenant_Status !== "Provisioning" && <MenuItem onClick={isDeleteEnabled ? handleSuspension : () => { }} sx={{ color: 'red' }} style={{ color: !isDeleteEnabled ? "#0000001f" : "" }}>Suspension</MenuItem>}
                          {tenant?.Tenant_Status !== "Decommissioned" && tenant?.Tenant_Status !== "Provisioning Failed" && tenant?.Tenant_Status !== "Active" && tenant?.Tenant_Status !== "Provisioning" && <MenuItem onClick={isDeleteEnabled ? handleReactivation : () => { }} sx={{ color: 'red' }} style={{ color: !isDeleteEnabled ? "#0000001f" : "" }}>Reactivation</MenuItem>}

                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 3. Decommison - Updated Dialog with Loading Feedback */}
      <Dialog
        open={isDecommissionOpen}
        onClose={decommissionLoading ? undefined : handleCloseDecommissionModal}
        PaperProps={{
          sx: { borderRadius: '16px', padding: '4px' }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" /> Confirm Action
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to decommission <strong>{menuTenant?.Tenant_Nm}</strong>?
            <br />
            <span style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
              Warning: This action cannot be undone and may affect associated resources.
            </span>
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleCloseDecommissionModal}
            color="inherit"
            disabled={decommissionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDecommissionAction}
            variant="contained"
            color="error"
            disabled={decommissionLoading}
            startIcon={decommissionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {decommissionLoading ? 'Processing...' : 'Decommission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 4. Suspension - Updated Dialog with Loading Feedback */}
      <Dialog
        open={isSuspensionOpen}
        onClose={suspensionLoading ? undefined : () => setIsSuspensionOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', padding: '4px' }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" /> Confirm Action
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to suspend the tenant <strong>{menuTenant?.Tenant_Nm}</strong>?
            <br />
            <span style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
              Warning: This action will immediately disable login access for all users across all instances and stop all API activity for this tenant.
            </span>
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={() => setIsSuspensionOpen(false)}
            color="inherit"
            disabled={suspensionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSuspensionAction}
            variant="contained"
            color="error"
            disabled={suspensionLoading}
            startIcon={suspensionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {suspensionLoading ? 'Processing...' : 'Suspension'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 5. Reactivation - Updated Dialog with Loading Feedback */}
      <Dialog
        open={isReactivationOpen}
        onClose={reactivationLoading ? undefined : () => setIsReactivationOpen(false)}
        PaperProps={{
          sx: { borderRadius: '16px', padding: '4px' }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" /> Confirm Action
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to reactivate the tenant <strong>{menuTenant?.Tenant_Nm}</strong>?
            <br />
            <span style={{ color: '#d32f2f', fontSize: '0.875rem' }}>
              Warning: User login access for all instances will be restored, and API integration will resume.
            </span>
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={() => setIsReactivationOpen(false)}
            color="inherit"
            disabled={reactivationLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReactivationAction}
            variant="contained"
            color="error"
            disabled={reactivationLoading}
            startIcon={reactivationLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {reactivationLoading ? 'Processing...' : 'Reactivation'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TenantList;
